import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { moveConnectionsKeys, seriesKeys } from '@/api/queryKeys'
import {
  fetchSeriesConnections,
  createConnection,
  deleteConnection,
  removeConnection,
  type CreateMoveConnectionDto,
} from '@/api/moveConnections'
import { useToast } from '@/hooks/use-toast'

/**
 * Hook to fetch all connections for a specific series
 *
 * @param seriesId - Series ID
 * @param includeRelations - Include related move and series data
 * @returns React Query result with array of connections
 *
 * @example
 * ```tsx
 * const { data: connections } = useSeriesConnections('series-id', true);
 * ```
 */
export function useSeriesConnections(
  seriesId: string,
  includeRelations?: boolean
) {
  return useQuery({
    queryKey: moveConnectionsKeys.bySeries(seriesId),
    queryFn: () => fetchSeriesConnections(seriesId, includeRelations),
    staleTime: 5 * 60 * 1000,
    enabled: !!seriesId,
  })
}

/**
 * Hook to create a new move connection
 *
 * Automatically invalidates:
 * - Series connections cache for the affected series
 * - Series detail cache (to update connection count)
 *
 * @returns Mutation object with async create function
 *
 * @example
 * ```tsx
 * const createMutation = useCreateConnection();
 * await createMutation.mutateAsync({
 *   seriesId: 'series-id',
 *   fromMoveId: 'move-1',
 *   toMoveId: 'move-2'
 * });
 * ```
 */
export function useCreateConnection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateMoveConnectionDto) => createConnection(data),
    onSuccess: newConnection => {
      // Invalidate series connections
      queryClient.invalidateQueries({
        queryKey: moveConnectionsKeys.bySeries(newConnection.seriesId),
      })
      // Invalidate series detail
      queryClient.invalidateQueries({
        queryKey: seriesKeys.detail(newConnection.seriesId),
      })
      toast({
        title: 'Connection created',
        description: 'Move connection has been created successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create connection',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to delete a connection by ID
 *
 * Automatically invalidates affected caches after successful deletion.
 *
 * @returns Mutation object with async delete function
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteConnection();
 * await deleteMutation.mutateAsync('connection-id');
 * ```
 */
export function useDeleteConnection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => deleteConnection(id),
    onSuccess: () => {
      // Invalidate all connections
      queryClient.invalidateQueries({
        queryKey: moveConnectionsKeys.all,
      })
      toast({
        title: 'Connection deleted',
        description: 'Move connection has been removed successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete connection',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to remove a specific connection by series and move IDs
 *
 * Alternative to useDeleteConnection when you have entity IDs instead of connection ID.
 *
 * @returns Mutation object with async remove function
 *
 * @example
 * ```tsx
 * const removeMutation = useRemoveConnection();
 * await removeMutation.mutateAsync({
 *   seriesId: 'series-id',
 *   fromMoveId: 'move-1',
 *   toMoveId: 'move-2'
 * });
 * ```
 */
export function useRemoveConnection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      seriesId,
      fromMoveId,
      toMoveId,
    }: {
      seriesId: string
      fromMoveId: string
      toMoveId: string
    }) => removeConnection(seriesId, fromMoveId, toMoveId),
    onSuccess: (_, variables) => {
      // Invalidate series connections
      queryClient.invalidateQueries({
        queryKey: moveConnectionsKeys.bySeries(variables.seriesId),
      })
      // Invalidate series detail
      queryClient.invalidateQueries({
        queryKey: seriesKeys.detail(variables.seriesId),
      })
      toast({
        title: 'Connection removed',
        description: 'Move connection has been removed successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove connection',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}
